<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import CheckboxList from '@/components/common/CheckboxList.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import OrdersDuplicationGrid from '@/components/orders/orders-duplication-grid.vue';
import ViewDuplicateOrderDetails, { type AccountItem } from '@/components/orders/view-duplicate-order-details.vue';
import { useBPLogger } from '@/composables/business-process-logger';
import { ButtonVariant } from '@/models/button-variant.enum';
import { CheckboxItem } from '@/models/checkbox-item';
import { PopUpSize } from '@/models/pop-up-size.enum';
import { PopUpType } from '@/models/pop-up-type.enum';
import type { ValidatorResult } from '@/models/validation-result.model';
import { OrdersService } from '@/services/orders.service';
import { useSelectedOrdersStore } from '@/stores/selected-grid-orders';
import { useUserStore } from '@/stores/user';
import {
  OrderCopyAccountErrorEnum,
  OrderCopyAccountResultDto,
  OrderCopyOrderErrorEnum,
  OrderCopyOrderResultDto,
  OrderCopyProductErrorEnum,
  OrderCopyProductResultDto,
  OrderCopyRequestDto,
  OrderCopyTargetDto,
  type AccountModel,
} from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const ACCOUNT_SELECTION_LIMIT = 10;

const { selectedAccountId, sfAccounts } = storeToRefs(useUserStore());
const router = useRouter();
const { removeSelectedOrders, getSelectedOrders } = useSelectedOrdersStore();
const { t } = useI18n();
const { pageViewBPLog } = useBPLogger();

const ordersService = new OrdersService();
const checkboxItems = ref<CheckboxItem[]>([]);
const loading = ref(true);
const showPreliminaryAccountSelection = ref(true);
const showSubmitPopup = ref(false);
const showErrorPopup = ref(false);
const accountsSelected = ref<string[]>([]);
const accountItems = ref<AccountItem[]>([]);
const orderCopyTarget = ref<OrderCopyTargetDto[]>([]);
const selectedAccountsLimitEnabled = computed<boolean>(() => {
  return accountsSelected.value.length > ACCOUNT_SELECTION_LIMIT;
});
const showAccountsLengthRestrictionPopUp = ref(false);
const submitEnabled = computed<boolean>(() => {
  return accountsSelected.value.length > 0 && accountsSelected.value.length <= ACCOUNT_SELECTION_LIMIT;
});
const gridLoading = ref(true);
const buttonDisabled = computed<boolean>(() => {
  return !submitEnabled.value || loading.value || gridLoading.value;
});

const submitAllowed = ref(false);
const showGrid = computed<boolean>(() => {
  return showPreliminaryAccountSelection.value ? true : submitAllowed.value;
});

watch(
  () => selectedAccountsLimitEnabled.value,
  (newVal: boolean) => {
    if (newVal) {
      showAccountsLengthRestrictionPopUp.value = true;
    }
  },
);

onMounted(() => {
  generateCheckboxItems(sfAccounts.value);
  loading.value = false;
  console.log(pageViewBPLog(true, { page_name: 'orders_duplication' }));
});

onBeforeMount(() => {
  window.addEventListener('popstate', handleBrowserBackButton);
});

onBeforeUnmount(() => {
  window.removeEventListener('popstate', handleBrowserBackButton);
});

async function validateOrdersForDuplication() {
  createOrderCopyTargetDto();

  loading.value = true;
  const validatorResult: ValidatorResult<OrderCopyAccountResultDto[]> = await ordersService.validateDuplicateOrders(
    { targets: orderCopyTarget.value },
    selectedAccountId.value,
  );

  if (validatorResult.isValid() && validatorResult.model) {
    showPreliminaryAccountSelection.value = false;
    generateCheckboxItems(
      sfAccounts.value.filter((acc) => accountsSelected.value.includes(acc.accountNumber)),
      validatorResult.model,
    );
    if (validatorResult.model.some((dto) => dto.orders.some((order) => order.products.length > 0))) {
      submitAllowed.value = true;
    }
  } else {
    showErrorPopup.value = true;
  }
  loading.value = false;
}

function createAccountCheckboxItem(account: AccountModel, info?: OrderCopyAccountResultDto): CheckboxItem {
  let notes: string[] = [];

  if (info) {
    notes = createNoteForAccountCheckboxItem(info);
  }

  const result = new CheckboxItem();
  result.id = account.accountNumber;
  result.name = account.name;
  result.checked = !!accountsSelected.value.find((accountSelected) => accountSelected === account.accountNumber);
  result.selectable = true;
  result.notes = notes;

  return result;
}

function createNoteForAccountCheckboxItem(info: OrderCopyAccountResultDto): string[] {
  if (info.error) {
    return [translateError(info.error)];
  }

  const orderErrors = info?.orders.map((order) => getOrderErrors(order));
  const productErrors = info?.orders.flatMap((order) => order?.products?.map((product) => getProductErrors(product)));

  return orderErrors.concat(productErrors);
}

function getOrderErrors(orderInfo: OrderCopyOrderResultDto): string {
  return orderInfo.error ? orderInfo.orderNumber + ' ' + translateError(orderInfo.error) : '';
}

function getProductErrors(productInfo: OrderCopyProductResultDto): string {
  return productInfo.error ? translateError(productInfo.error, productInfo.name) : '';
}

function translateError(error: string, productName?: string): string {
  switch (error) {
    case OrderCopyProductErrorEnum.AssortmentUnavailable:
      return t('viewOrders.order_duplication.errors.assortment_unavailable', { productName });
    case OrderCopyAccountErrorEnum.UserUnauthorized:
      return t('viewOrders.order_duplication.errors.user_unauthorized');
    case OrderCopyAccountErrorEnum.DuplicateAccount:
      return t('viewOrders.order_duplication.errors.duplicate_account');
    case OrderCopyOrderErrorEnum.AssortmentFetchFailed:
      return t('viewOrders.order_duplication.errors.assortment_fetch_failed');
    case OrderCopyOrderErrorEnum.OrderNotFound:
      return t('viewOrders.order_duplication.errors.order_not_found');
    case OrderCopyOrderErrorEnum.OrderTypeUnavailable:
      return t('viewOrders.order_duplication.errors.order_type_unavailable');
    case OrderCopyOrderErrorEnum.DraftCreationFailed:
      return t('viewOrders.order_duplication.errors.draft_creation_failed');
    case OrderCopyOrderErrorEnum.NoAssortmentAvailable:
      return t('viewOrders.order_duplication.errors.no_assortment_available');
    case OrderCopyOrderErrorEnum.OrderTypeNotDuplicatable:
      return t('viewOrders.order_duplication.errors.order_type_not_duplicatable');
    default:
      return t('global.error');
  }
}

function generateCheckboxItems(accounts: AccountModel[], resultDto?: OrderCopyAccountResultDto[]) {
  checkboxItems.value = [];

  for (const index in accounts) {
    const checkboxItem = createAccountCheckboxItem(
      accounts[index],
      resultDto?.find((dto) => dto.accountId === accounts[index].accountNumber),
    );

    if (checkboxItem) {
      checkboxItems.value.push(checkboxItem);
    }
  }

  return checkboxItems;
}

function createOrderCopyTargetDto() {
  orderCopyTarget.value = [];
  const selectedOrderNumbers = getSelectedOrders().map((order) => order.orderNumber);

  for (const index in accountsSelected.value) {
    const orderCopyTargetDto = new OrderCopyTargetDto();
    orderCopyTargetDto.accountId = accountsSelected.value[index];
    orderCopyTargetDto.orderNumbers = selectedOrderNumbers;

    orderCopyTarget.value.push(orderCopyTargetDto);
  }
}

function createSubmissionMessage(orderCopyAccountResultDto: OrderCopyAccountResultDto[] | undefined) {
  if (!orderCopyAccountResultDto) {
    return;
  }

  for (const index in orderCopyAccountResultDto) {
    const accountInfo = orderCopyAccountResultDto[index];
    const accountId = accountInfo.accountId;

    if (accountsSelected.value.includes(accountId)) {
      const accountName = checkboxItems.value.find((item) => item.id === accountId)!.name;
      const draftNumbers = accountInfo.orders.map((order) => order.draftNumber);
      const accountItem = { accountId, accountName, draftNumbers } as AccountItem;
      accountItems.value.push(accountItem);
    }
  }
}

function closeAccountsLengthRestrictionPopUp() {
  showAccountsLengthRestrictionPopUp.value = false;
}

async function submitOrdersForDuplication() {
  loading.value = true;

  const orderCopyRequestDto = new OrderCopyRequestDto();
  orderCopyRequestDto.targets = orderCopyTarget.value;

  const validatorResult = await ordersService.duplicateOrders(orderCopyRequestDto, selectedAccountId.value);

  if (validatorResult.isValid() && validatorResult.model) {
    createSubmissionMessage(validatorResult.model);
    showSubmitPopup.value = true;
  } else {
    showErrorPopup.value = true;
  }

  loading.value = false;
  removeSelectedOrders();
}

export interface NotifierButton {
  text: string;
  action?: () => Promise<void>;
  variant?: ButtonVariant;
}

function handleBrowserBackButton() {
  router.replace('/');
}

function closePopup() {
  if (showSubmitPopup.value || showErrorPopup.value) {
    router.replace('/');
  }
  showSubmitPopup.value = false;
  showErrorPopup.value = false;
}

function gridLoaded() {
  gridLoading.value = false;
}

function handleBackButton() {
  if (showPreliminaryAccountSelection.value) {
    router.go(-1);
  } else {
    showPreliminaryAccountSelection.value = true;
    generateCheckboxItems(sfAccounts.value);
  }
}
</script>

<template>
  <div class="view-orders-duplication">
    <div class="header">
      <div class="title-cnt">
        <BackstageIcon
          name="arrow-left"
          class="back-icon"
          @click="handleBackButton"
          data-cy="order-duplication-back-button" />
        <h3 v-if="showPreliminaryAccountSelection" class="page-title" data-cy="orders-duplicate-title">
          {{ t('viewOrders.order_duplication.select_accounts_title') }}
        </h3>
        <h3 v-else class="page-title" data-cy="orders-duplicate-title">
          {{ t('viewOrders.order_duplication.duplicate_orders_title') }}
        </h3>
      </div>
      <BackstageButton
        v-if="showPreliminaryAccountSelection"
        :disabled="buttonDisabled"
        icon-right="arrow-right"
        v-on:click="validateOrdersForDuplication()">
        {{ $t('global.next_button') }}
      </BackstageButton>
      <BackstageButton
        v-else
        :disabled="buttonDisabled || !submitAllowed"
        icon-right="arrow-right"
        v-on:click="submitOrdersForDuplication()">
        {{ $t('global.submit_reminder_popup.submit_button') }}
      </BackstageButton>
    </div>
    <div v-if="loading" class="loader"></div>
    <div v-else>
      <div class="form">
        <CheckboxList :items="checkboxItems" v-model:selected="accountsSelected"> </CheckboxList>
        <div v-if="submitAllowed" class="note">
          <p>{{ t('viewOrders.order_duplication.update_note') }}</p>
        </div>
        <OrdersDuplicationGrid v-if="showGrid" :enable-pagination="false" v-on:grid-loading-done="gridLoaded()" />
        <div v-if="showAccountsLengthRestrictionPopUp">
          <NotificationMessage
            :size="PopUpSize.Small"
            :type="PopUpType.Warning"
            v-on:close="closeAccountsLengthRestrictionPopUp">
            <template v-slot:default>
              <div v-html="t('viewOrders.order_duplication.accounts_restriction_note')"></div>
            </template>
          </NotificationMessage>
        </div>
        <div v-if="showSubmitPopup">
          <NotificationMessage
            :title="t('viewOrders.order_duplication.submit_popup_title')"
            :size="'large'"
            data-cy="order-duplication-submit-popup"
            v-on:close="closePopup">
            <ViewDuplicateOrderDetails :items="accountItems" v-on:close-popup="closePopup()">
            </ViewDuplicateOrderDetails>
          </NotificationMessage>
        </div>
        <div v-if="showErrorPopup">
          <NotificationMessage :title="t('global.error')" :size="'large'" v-on:close="closePopup">
          </NotificationMessage>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.buttons-container {
  display: flex;
  justify-content: flex-end;
}

.view-orders-duplication {
  @include common-container;
  min-height: 100vh;
  padding-top: 30px;

  .header {
    display: flex;
    justify-content: space-between;
    padding: 10px;
  }

  .title-cnt {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;

    .action-buttons {
      display: flex;
      gap: 10px;
    }
  }

  .form {
    @include container-background;
    .note {
      display: flex;
      width: fit-content;
      padding-left: 10px;
      padding-right: 15px;
      color: var(--primary-label-input-color);
    }
  }

  .nav-pills .buttons-menu {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
}

.selector-button-box {
  display: flex;
}

.orders-duplication-grid:deep() {
  .draft-highlight {
    background: var(--row-highlight-color) !important;
  }

  .hold {
    color: var(--special-red-color-darker);
  }

  .gray-row {
    color: var(--light-gray-color);
  }

  .ag-theme-balham {
    width: 100%;
    height: 100%;
  }

  .grid-cnt {
    padding: 0;
    border-top: 1px var(--gray-color)-border solid;
    border-bottom: 1px var(--gray-color)-border solid;
  }
}
</style>
